import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FilterType } from './models';
import { SingleFilterBarService } from './single-filter-bar.service';

@Component({
  selector: 'miq-single-filter-bar',
  template: `
    <miq-internal-single-filter-bar
      [selected]="currentSelection"
      [filters]="currentFilters$ | async"
      [useProgrammaticChanges]="useProgrammaticChanges"
      (itemSelected)="itemSelected($event)"
    >
    </miq-internal-single-filter-bar>
  `,
  standalone: false,
})
export class SingleFilterBarComponent implements OnChanges, OnDestroy {
  /**
   * All the filters that will be displayed in the filter bar
   */
  @Input() public filters: FilterType[] | string[];
  /**
   * The current selection, if provided and there is nothing saved in the storage,
   * this value will be selected, otherwise the stored value
   */
  @Input() public selection: string | number;
  /**
   * The unique indentifier for the local storage, to keep the selection when navigating back
   */
  @Input() public stateIdentifier: string;
  /**
   * When this flag is true, the component will not emit the selectionChange event when the selection is changed programmatically (through input binding).
   * This is useful to prevent emitting events when the parent component is updating the selection based on some logic, and only emit when the user changes the selection through the UI.
   */
  @Input() useProgrammaticChanges = false;
  @Output() public selectionChange = new EventEmitter();

  public currentFilters$: BehaviorSubject<FilterType[]> = new BehaviorSubject([]);
  public currentSelection: string | number;

  constructor(private service: SingleFilterBarService) {}

  ngOnChanges({ filters }: SimpleChanges) {
    // If filters changed and has a current value
    if (filters && filters.currentValue) {
      this.processFilters();
    }

    this.processSelectedValue();
  }

  ngOnDestroy() {
    this.currentFilters$.complete();
  }

  public itemSelected(key: string) {
    this.currentSelection = key;
    this.saveSelectionInStorage();
    this.selectionChange.emit(key);
  }

  private saveSelectionInStorage() {
    this.service.setRecordTabState(this.stateIdentifier, this.currentSelection);
  }

  private getStoredState() {
    return this.service.getRecordTabState(this.stateIdentifier);
  }

  private processSelectedValue() {
    const existingState = this.getStoredState();
    /*
     * It is possible for the filters values to change as the user can add or
     * delete usages from the details page. The biggest impact happens when
     * the user deletes a usage. If the user previously selected the usage that
     * now doesn't exist, the following logic that may use the selection may be
     * broken. Therefore, checking that the selected option is still valid is
     * required.
     */
    const isSelectionValid = this.filters?.some(filter => filter === existingState);
    if (existingState && isSelectionValid) {
      this.currentSelection = existingState;
      this.selectionChange.emit(this.currentSelection);
    } else {
      this.currentSelection = this.selection;
      this.selectionChange.emit(this.currentSelection);
    }
  }

  private processFilters() {
    if (this.filters.length > 0) {
      // Takes the first element to check the type
      const firstFilter = this.filters[0];
      // If the element is not an object converts the strings into filter type
      if (typeof firstFilter !== 'object') {
        const newFilters = (this.filters as string[]).map(x => {
          return { key: x, value: x };
        });
        this.setFilters(newFilters);
      } else {
        this.setFilters(this.filters as FilterType[]);
      }
    } else {
      // When the input filter doesnt have values
      this.clearFilters();
    }
  }

  private clearFilters() {
    this.currentFilters$.next([]);
  }

  private setFilters(newFilters: FilterType[]) {
    this.currentFilters$.next(newFilters);
  }
}
